import { useCallback } from "react";
import { CropFrame, CornerHandle, DarkOverlaySide, OverlayContainer } from "../style";

const RESIZE_HANDLES = [
  { id: "nw", style: { top: -5, left: -5 },                                    cursor: "nw-resize" },
  { id: "n",  style: { top: -5, left: "50%", transform: "translateX(-50%)" },  cursor: "n-resize"  },
  { id: "ne", style: { top: -5, right: -5 },                                   cursor: "ne-resize" },
  { id: "e",  style: { top: "50%", right: -5, transform: "translateY(-50%)" }, cursor: "e-resize"  },
  { id: "se", style: { bottom: -5, right: -5 },                                cursor: "se-resize" },
  { id: "s",  style: { bottom: -5, left: "50%", transform: "translateX(-50%)" }, cursor: "s-resize" },
  { id: "sw", style: { bottom: -5, left: -5 },                                 cursor: "sw-resize" },
  { id: "w",  style: { top: "50%", left: -5, transform: "translateY(-50%)" }, cursor: "w-resize" },
];

/**
 * DraggableFrame
 *
 * Pointer Events + setPointerCapture + rAF-throttled deltas. Reports the
 * absolute pointer delta since drag-start (not per-frame deltas), so the
 * parent applies it to a fixed start-crop and the frame never accumulates
 * rounding error or drifts behind the cursor.
 *
 * Props:
 *   frameRect    { x, y, width, height }   — display pixels (relative to video element)
 *   videoBounds  { width, height }
 *   onDragStart()                          — fired once on pointer-down
 *   onDrag(dx, dy)                         — absolute delta from drag-start, in display px
 *   onResizeStart(handle)
 *   onResize(handle, dx, dy)               — absolute delta from drag-start, in display px
 *   onDragEnd()
 */
export default function DraggableFrame({
  frameRect,
  videoBounds,
  onDragStart,
  onDrag,
  onResizeStart,
  onResize,
  onDragEnd,
}) {
  const beginPointerDrag = useCallback((e, onMoveAbs) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);

    const startX = e.clientX;
    const startY = e.clientY;
    let rafId = 0;
    let pdx = 0;
    let pdy = 0;

    const flush = () => {
      rafId = 0;
      onMoveAbs(pdx, pdy);
    };

    const onMove = (me) => {
      pdx = me.clientX - startX;
      pdy = me.clientY - startY;
      if (!rafId) rafId = requestAnimationFrame(flush);
    };

    const onEnd = () => {
      if (rafId) cancelAnimationFrame(rafId);
      onMoveAbs(pdx, pdy); // ensure final position is committed
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
      onDragEnd?.();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
  }, [onDragEnd]);

  const handleBodyPointerDown = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    onDragStart?.();
    beginPointerDrag(e, (dx, dy) => onDrag(dx, dy));
  }, [beginPointerDrag, onDragStart, onDrag]);

  const handleResizePointerDown = useCallback((e, handle) => {
    onResizeStart?.(handle);
    beginPointerDrag(e, (dx, dy) => onResize(handle, dx, dy));
  }, [beginPointerDrag, onResizeStart, onResize]);

  if (!frameRect || !videoBounds) return null;

  const { x, y, width, height } = frameRect;
  const { width: vw, height: vh } = videoBounds;

  const overlays = [
    { left: 0,         top: 0,          width: x,                              height: vh },
    { left: x + width, top: 0,          width: Math.max(0, vw - x - width),    height: vh },
    { left: x,         top: 0,          width: width,                           height: y  },
    { left: x,         top: y + height, width: width, height: Math.max(0, vh - y - height) },
  ];

  return (
    <OverlayContainer>
      {overlays.map((s, i) => (
        <DarkOverlaySide key={i} style={{ left: s.left, top: s.top, width: s.width, height: s.height }} />
      ))}

      <CropFrame
        style={{ left: x, top: y, width, height, touchAction: "none" }}
        onPointerDown={handleBodyPointerDown}
      >
        {RESIZE_HANDLES.map(({ id, style, cursor }) => (
          <CornerHandle
            key={id}
            style={{ ...style, cursor, pointerEvents: "all", touchAction: "none" }}
            onPointerDown={(e) => handleResizePointerDown(e, id)}
          />
        ))}
      </CropFrame>
    </OverlayContainer>
  );
}
